'use client';

/*
  Silences the benign "ResizeObserver loop completed with undelivered notifications" error.
  This error occurs when ResizeObserver cannot deliver all observations within a single animation frame.
  It's harmless and commonly triggered by UI libraries with dynamic sizing.
  See: https://github.com/WICG/resize-observer/issues/38
*/
import { useEffect } from 'react';

const RESIZE_OBSERVER_ERROR = 'ResizeObserver loop';

export function useResizeObserverErrorSilencer() {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const originalOnError = window.onerror;

    window.onerror = (message, source, lineno, colno, error) => {
      if (
        typeof message === 'string' &&
        message.includes(RESIZE_OBSERVER_ERROR)
      ) {
        return true;
      }
      return originalOnError?.(message, source, lineno, colno, error) ?? false;
    };

    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes(RESIZE_OBSERVER_ERROR)) {
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener('error', handleError);

    return () => {
      window.onerror = originalOnError;
      window.removeEventListener('error', handleError);
    };
  }, []);
}

export default function ResizeObserverErrorSilencer() {
  useResizeObserverErrorSilencer();
  return null;
}
